import { useCallback, useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { STANDARD_PACKAGE_ID } from '../../../../../../services/package-service';
import Global from '../../../../../../store/global-selectors';
import { useFulfillmentPlannerContext } from '../../../../../../fulfillment-planner/context';

import { messages } from '../messages';
import type { BoxResult } from '../types';
import { getAppliedState, markPackingApplied, onAppliedChange, resetPackingApplied } from '../use-packing-applied';
import type { JobResultResponse } from '../../../../../../packing/services/packing-service';

const DIMENSION_MATCH_TOLERANCE = 0.5;

type FulfillmentPlannerContextCallbacks = {
  onPackageChange?: (packageId: string, dimensions: Record<string, unknown>) => void;
  onWeightAndDimensionsChange?: (weight: { value: number; unit: string }, dimensions: null | Record<string, unknown>) => void;
  onMultiPackageSave?: (packages: unknown[]) => void;
};

type UseApplyRecommendationReturn = {
  handleApply: () => void;
  isApplied: boolean;
  appliedPackageName: string | null;
  resetApplied: () => void;
};

export const useApplyRecommendation = (
  result: JobResultResponse | null,
  availablePackages: Array<Record<string, unknown>>,
  fulfillmentPlanId: string,
  sellerDimensionUnit: string,
  sellerWeightUnit: string,
): UseApplyRecommendationReturn => {
  const intl = useIntl();
  const context = useFulfillmentPlannerContext();
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));

  const persistedState = getAppliedState(fulfillmentPlanId);
  const [isApplied, setIsApplied] = useState(persistedState?.isApplied ?? false);
  const [appliedPackageName, setAppliedPackageName] = useState<string | null>(persistedState?.packageName ?? null);
  const [appliedValues, setAppliedValues] = useState<Array<{
    packageId?: string;
    weight?: number;
    dimensions?: Record<string, number>;
  }> | null>(null);
  const isApplyingRef = useRef(false);
  const pendingWeightRef = useRef<{ value: number; unit: string } | null>(null);
  const expectedPackageIdRef = useRef<string | null>(null);

  useEffect(() => {
    return onAppliedChange((fpId, packageName) => {
      if (fpId !== fulfillmentPlanId) return;
      if (packageName === null) {
        setIsApplied(false);
        setAppliedPackageName(null);
        setAppliedValues(null);
        isApplyingRef.current = false;
        pendingWeightRef.current = null;
        expectedPackageIdRef.current = null;
      }
    });
  }, [fulfillmentPlanId]);

  const { onPackageChange, onWeightAndDimensionsChange, onMultiPackageSave } = (context ??
    emptyObject) as FulfillmentPlannerContextCallbacks;

  const findPackageByBox = useCallback(
    (box: BoxResult['box']) => {
      if (!box) return null;

      if (box.packageId) {
        const byId = availablePackages.find((pkg: { packageId?: string }) => pkg.packageId === box.packageId);
        if (byId) return byId;
      }

      if (box.name) {
        const byName = availablePackages.find((pkg: { name?: string }) => pkg.name?.toLowerCase() === box.name?.toLowerCase());
        if (byName) return byName;
      }

      if (box.length && box.width && box.height) {
        const byDimensions = availablePackages.find(
          (pkg: { dimensions?: Record<string, unknown>; length?: number; width?: number; height?: number }) => {
            const pkgDims = pkg.dimensions ?? {};
            const pkgLength = Number(pkgDims.length) || Number(pkg.length) || 0;
            const pkgWidth = Number(pkgDims.width) || Number(pkg.width) || 0;
            const pkgHeight = Number(pkgDims.height) || Number(pkg.height) || 0;

            return (
              Math.abs(pkgLength - box.length) <= DIMENSION_MATCH_TOLERANCE &&
              Math.abs(pkgWidth - box.width) <= DIMENSION_MATCH_TOLERANCE &&
              Math.abs(pkgHeight - box.height) <= DIMENSION_MATCH_TOLERANCE
            );
          },
        );
        if (byDimensions) return byDimensions;
      }

      return null;
    },
    [availablePackages],
  );

  const formatBoxDimensions = useCallback(
    (box: BoxResult['box']) => {
      if (!box) return null;
      return {
        length: Math.round(box.length * 10) / 10,
        width: Math.round(box.width * 10) / 10,
        height: Math.round(box.height * 10) / 10,
        unit: sellerDimensionUnit,
      };
    },
    [sellerDimensionUnit],
  );

  const calculateTotalWeight = useCallback(
    (itemPositions: BoxResult['itemPositions']) => {
      const totalWeight = itemPositions?.reduce((sum, pos) => sum + (pos.item?.weight ?? 0), 0) ?? 0;
      const weightValue = Math.round(totalWeight * 100) / 100;
      return { value: weightValue, unit: sellerWeightUnit };
    },
    [sellerWeightUnit],
  );

  const buildCompositePackageId = useCallback(
    (selectedPackage: { packageTypeId?: string; isCustom?: boolean; carrierId?: number } | null) => {
      if (!selectedPackage) {
        return `${STANDARD_PACKAGE_ID}-false-0`;
      }
      return `${selectedPackage.packageTypeId}-${selectedPackage.isCustom ?? false}-${selectedPackage.carrierId ?? 0}`;
    },
    [],
  );

  const convertBoxToPackage = useCallback(
    (boxResult: BoxResult) => {
      const box = boxResult?.box;
      if (!box) return null;

      const selectedPackage = findPackageByBox(box) as Record<string, unknown> | null;
      const dimensions = formatBoxDimensions(box);
      const weight = calculateTotalWeight(boxResult.itemPositions);

      return {
        packageId: (selectedPackage?.packageId as string) ?? buildCompositePackageId(selectedPackage),
        packageTypeId: selectedPackage?.packageTypeId ?? STANDARD_PACKAGE_ID,
        carrierId: selectedPackage?.carrierId ?? null,
        isCustom: selectedPackage?.isCustom ?? true,
        name: selectedPackage?.name ?? box.name,
        dimensions,
        weight,
        insuredValue: { value: 0, code: 'USD' },
      };
    },
    [findPackageByBox, formatBoxDimensions, calculateTotalWeight, buildCompositePackageId],
  );

  const handleApply = useCallback(() => {
    if (!result) return;

    const applyBoxes = result?.boxes ?? emptyArray;
    const isMultiBox = result?.isMultiBox && applyBoxes.length > 1;
    const packageName = isMultiBox
      ? intl.formatMessage(messages.packagesConfigured, { count: applyBoxes.length })
      : intl.formatMessage(messages.packageConfigured, { name: result?.optimalBox?.name ?? '' });

    if (isMultiBox) {
      const packages = applyBoxes.map(convertBoxToPackage).filter(Boolean);
      if (!packages.length || !onMultiPackageSave) return;
      onMultiPackageSave(packages);
      isApplyingRef.current = true;
    } else {
      const optimalBox = result?.optimalBox;
      const selectedPackage = findPackageByBox(optimalBox) as Record<string, unknown> | null;
      const dimensions = formatBoxDimensions(optimalBox);
      const weight = calculateTotalWeight(result?.itemPositions);
      const compositePackageId = (selectedPackage?.packageId as string) ?? buildCompositePackageId(selectedPackage);

      onPackageChange?.(compositePackageId, dimensions ?? emptyObject);

      if (weight.value > 0) {
        pendingWeightRef.current = weight;
        expectedPackageIdRef.current = compositePackageId;
      } else {
        isApplyingRef.current = true;
      }
    }

    setAppliedPackageName(packageName ?? null);
    setIsApplied(true);

    markPackingApplied({ fulfillmentPlanId, packageName: packageName ?? '' });
  }, [
    result,
    fulfillmentPlanId,
    intl,
    findPackageByBox,
    formatBoxDimensions,
    calculateTotalWeight,
    buildCompositePackageId,
    convertBoxToPackage,
    onPackageChange,
    onMultiPackageSave,
  ]);

  const resetApplied = useCallback(() => {
    resetPackingApplied(fulfillmentPlanId);
    setIsApplied(false);
    setAppliedPackageName(null);
    setAppliedValues(null);
    isApplyingRef.current = false;
    pendingWeightRef.current = null;
    expectedPackageIdRef.current = null;
  }, [fulfillmentPlanId]);

  // Change detection: reset applied state when user manually changes package/weight/dimensions
  useEffect(() => {
    const packages = fulfillmentPlan?.packages ?? emptyArray;
    const currentPackageId = packages[0]?.packageId;
    const currentPackageCount = packages.length;

    if (pendingWeightRef.current && expectedPackageIdRef.current) {
      if (currentPackageId === expectedPackageIdRef.current) {
        onWeightAndDimensionsChange?.(pendingWeightRef.current, null);
        pendingWeightRef.current = null;
        expectedPackageIdRef.current = null;
        isApplyingRef.current = true;
      }
      return;
    }

    if (isApplyingRef.current) {
      setAppliedValues(
        packages.map((p: { packageId?: string; weight?: { value?: number }; dimensions?: Record<string, number> }) => ({
          packageId: p.packageId,
          weight: p.weight?.value,
          dimensions: p.dimensions,
        })),
      );
      isApplyingRef.current = false;
      return;
    }

    if (!isApplied || !appliedValues) {
      return;
    }

    const packageCountChanged = currentPackageCount !== appliedValues.length;
    if (packageCountChanged) {
      resetApplied();
      return;
    }

    const hasAnyChange = packages.some(
      (p: { packageId?: string; weight?: { value?: number }; dimensions?: Record<string, number> }, idx: number) => {
        const saved = appliedValues[idx];
        if (!saved) return true;
        if (p.packageId !== saved.packageId) return true;
        if (p.weight?.value !== undefined && saved.weight !== undefined && Math.abs(p.weight.value - saved.weight) > 0.01) return true;
        if (
          p.dimensions &&
          saved.dimensions &&
          (Math.abs((p.dimensions.length ?? 0) - (saved.dimensions.length ?? 0)) > 0.1 ||
            Math.abs((p.dimensions.width ?? 0) - (saved.dimensions.width ?? 0)) > 0.1 ||
            Math.abs((p.dimensions.height ?? 0) - (saved.dimensions.height ?? 0)) > 0.1)
        )
          return true;
        return false;
      },
    );

    if (hasAnyChange) {
      resetApplied();
    }
  }, [fulfillmentPlan, isApplied, appliedValues, fulfillmentPlanId, onWeightAndDimensionsChange, resetApplied]);

  return { handleApply, isApplied, appliedPackageName, resetApplied };
};
